import React, { FC, useEffect, useState, ChangeEvent } from "react";
import { useSearchParams } from "react-router-dom";
import { GoogleMap, useJsApiLoader, MarkerF } from "@react-google-maps/api";
import Autocomplete from "react-google-autocomplete";
import { useForm, SubmitHandler, Controller } from "react-hook-form";
import Dropdown from "react-bootstrap/Dropdown";
import { toast } from "react-toastify";
import "rc-slider/assets/index.css";

import "./add-golf-course.scss";
import { useAppDispatch, useAppSelector } from "../../../../app/hooks";
import {
  CLUB_ACTIVE_STATUS,
  CLUB_ACCOUNT_NAME_STEP,
  CLUB_LINK_TEE_TIME_BOOKING_SYSTEM_STEP,
  CLUB_LOCATION_MAP_STEP,
  CLUB_PICTURES_STEP,
  CLUB_DESCRIPTION_STEP,
  COURSE_BASICS_STEP,
  CLUB_FACILITIES_STEP,
  COURSE_TIME_AND_DAYS_STEP,
  CLUB_PREVIEW_STEP,
  COURSE_DISCOUNT_STEP,
  SAGA_STATUS_SUCCESS
} from "../../../../utils/constants";

import { clubsActions, fetchCreateClubCourseStatus, fetchUpdateClubStatus } from "../../../../reducers/clubsSlice";
import { externalClubsActions, fetchexternalClubs } from '../../../../reducers/externalClubsSlice';
import { merchantFilesActions, fetchSelectedMerchantFiles } from "../../../../reducers/merchantFilesSlice";
import { coursesActions, fetchUpdateCourseDataStatus } from "../../../../reducers/coursesSlice";
import { discountActions, fetchDiscountProfiles } from "../../../../reducers/discountsSlice";

import { ClubPayload, ClubTimings } from "../../../../models/clubs";
import { CoursesPayload } from "../../../../models/courses";
import { ExternalClubPayload } from "../../../../models/externalClubs";
import { MerchantFiles } from "../../../../models/merchantFiles";

import Prompt from "../../../../shared/prompt/prompt";

import AddDiscount from "../add-discount/add-discount";
import LinkTeeTimeComponent from "../link-tee-time-component/link-tee-time-component";
import CoursePreview from "../course-preview/course-preview";
import CourseTimings from "../course-timings/course-timings";
import { getUniqueString } from "../../../../utils/common";
import { useTranslation } from "react-i18next";

const libraries: any = ['places'];

const ALLOWED_MERCHANT_IMAGES = 5;

interface AddGolfCourseProps {
  handleTileChange: (arg: any) => void;
  setStep: (arg: any) => void;
  activeStep: number;
  navigateToPath: (path: string) => void;
  editClubCourseData: boolean;
  club?: ClubPayload;
  course?: CoursesPayload;
}

interface ClubName {
  clubDisplayName: string;
}

interface clubLocation {
  clubName: string;
}
interface clubBasicStepsPayload {
  courseHoles: number;
  coursePar: number;
  courseSlope: number;
  courseDistance: number;
  courseRating: number;
}

interface clubDescriptionStepsPayload {
  clubDescription: string;
}

interface clubAmenitiesPayload {
  clubWalkingAround: boolean;
  clubMetalSpikesAllowed: boolean;
  clubDressCodeRequired: boolean;
  clubCartsProvided: boolean;
  clubGolfClubsProvided: boolean;
  clubCaddiesAvailable: boolean;
  clubDrivingRangePresent: boolean;
  clubPitchingAreaPresent: boolean;
  clubPuttingGreen: boolean;
  clubTeachingPro: boolean;
}

const AddGolfCourse: FC<AddGolfCourseProps> = ({
  handleTileChange,
  setStep,
  activeStep,
  navigateToPath,
  editClubCourseData = false,
  club,
  course
}) => {

  const dispatch = useAppDispatch();
  const [searchParams] = useSearchParams();

  const { isLoaded } = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey: "AIzaSyDoMcuVhHHP8fos2Vp3PdtdZNEaSYHq9kk",
    libraries
  });

  // if edit step is passed then in the path only show the only view 
  const editStep = searchParams.get("step");

  const [showExternalClubModal, toggleExternalClubModal] = useState(false);

  const {
    register: clubDisplayNameRegister,
    handleSubmit: handleClubDisplayNameSubmit,
    formState: { errors: clubDisplayNameError },
    reset: resetClubDisplayName,
    control: clubDisplayNameControl,
  } = useForm<ClubName>();
  const onClubDisplayNameSubmit: SubmitHandler<ClubName> = () => nextStep();

  const [clubDisplayName, setClubDisplayName] = useState("");
  const [formattedClubName, setformattedClubName] = useState("");
  const [clubAddressOne, setClubAddressOne] = useState("");
  const [clubCity, setClubCity] = useState("");
  const [clubProvince, setClubProvince] = useState("");
  const [clubPostalCode, setClubPostalCode] = useState("");
  const [clubCountry, setClubCountry] = useState("");
  const [clubLocationLat, setClubLocationLat] = useState(0);
  const [clubLocationLong, setclubLocationLong] = useState(0);
  const [clubLocationErr, updateClubLocationErr] = useState<string>("");

  // description view form object and hooks
  const {
    register: descriptionRegister,
    handleSubmit: handleDescriptionSubmit,
    formState: { errors: descriptionError },
    reset: resetDescription,
  } = useForm<clubDescriptionStepsPayload>();
  const onDescriptionSubmit: SubmitHandler<clubDescriptionStepsPayload> = () => nextStep();

  const [clubDescription, setClubDescription] = useState("");

  // basic course view form object and hooks
  const [courseHoles, setcourseHoles] = useState(0);
  const [coursePar, setCoursePar] = useState(0);
  const [courseDistance, setCourseDistance] = useState(0);
  const [courseSlope, setCourseSlope] = useState(0);
  const [courseRating, setCourseRating] = useState(0);

  const {
    register: basicRegister,
    handleSubmit: handleCourseBasicSubmit,
    formState: { errors: basicErrors },
    reset: resetBasicSteps,
  } = useForm<clubBasicStepsPayload>();
  const onClubBasicSubmit: SubmitHandler<clubBasicStepsPayload> = () => nextStep();

  // Club amenities form object and hooks
  const [clubWalkingAround, setClubWalkingAround] = useState(false);
  const [clubMetalSpikesAllowed, setClubMetalSpikesAllowed] = useState(false);
  const [clubDressCodeRequired, setClubDressCodeRequired] = useState(false);
  const [clubCartsProvided, setClubCartsProvided] = useState(false);
  const [clubGolfClubsProvided, setClubGolfClubsProvided] = useState(false);
  const [clubDrivingRangePresent, setClubDrivingRangePresent] = useState(false);
  const [clubCaddiesAvailable, setClubCaddiesAvailable] = useState(false);
  const [clubPitchingAreaPresent, setClubPitchingAreaPresent] = useState(false);
  const [clubPuttingGreen, setClubPuttingGreen] = useState(false);
  const [clubTeachingPro, setClubTeachingPro] = useState(false);
  const [requestedDataIsNotFilled, setRequestedDataIsNotFilled] = useState(false);

  const {
    register: amenitiesRegister,
    handleSubmit: handleClubAmenitiesSubmit,
    formState: { errors: amenitiesErrors },
    reset: resetClubAmenities,
  } = useForm<clubAmenitiesPayload>();

  const createClubCourseStatus: string = useAppSelector(fetchCreateClubCourseStatus);
  const updateClubStatus: string = useAppSelector(fetchUpdateClubStatus);
  const updateCourseDataStatus: string = useAppSelector(fetchUpdateCourseDataStatus);
  const { t } = useTranslation("")

  const externalClubs: Array<ExternalClubPayload> =
    useAppSelector(fetchexternalClubs);
  const merchantImages: Array<MerchantFiles> = useAppSelector(
    fetchSelectedMerchantFiles
  );

  const disableBgColor =
    merchantImages.length === ALLOWED_MERCHANT_IMAGES
      ? "uploadImageWrap--disabled"
      : "";

  const onClubAccountNameSubmit = () => {

    if (club && course) {

      // update club info
      let dataUpdated = false;
      const { name } = club;
      if (clubDisplayName && clubDisplayName.trim().length && name !== clubDisplayName) {
        let updatePayload: any = {
          name: clubDisplayName.trim(),
          ["_id"]: club._id,
          clubEditStep: CLUB_ACCOUNT_NAME_STEP
        };
        dispatch(clubsActions.updateClubData(updatePayload));
        dataUpdated = true;
      }

      // update course info from external club object
      if (course) {
        // update course info
        const { par, distance, slope, rating } = course;

        let courseUpdatePayload: any = {
          ...(coursePar && par != coursePar ? { par: coursePar } : {}),
          ...(courseDistance && distance != courseDistance
            ? { distance: courseDistance }
            : {}),
          ...(courseSlope && slope != courseSlope ? { slope: courseSlope } : {}),
          ...(courseRating && rating != courseRating ? { rating: courseRating } : {}),
        };

        console.log('courseUpdatePayload is---', courseUpdatePayload)

        if (Object.keys(courseUpdatePayload).length) {
          courseUpdatePayload["_id"] = course._id;
          dispatch(coursesActions.updateCourseData(courseUpdatePayload));
          dataUpdated = true;
        }
      }

      // if no data is updated then immediately move to next step
      if (!dataUpdated) {
        moveToNextStep();
      }

    } else {

      // if club object not present then create new club object
      const clubObject: any = {
        clubId: Date.now(),    // create club id
        ...(clubDisplayName ? { name: clubDisplayName } : {}),
        ...(clubAddressOne ? { addressOne: clubAddressOne } : {}),
        ...(clubCity ? { city: clubCity } : {}),
        ...(clubCountry ? { countryName: clubCountry } : {}),
        ...(clubProvince ? { provinceName: clubProvince } : {}),
      };

      const courseObject: any = {
        ...(courseDistance ? { distance: courseDistance } : {}),
        ...(coursePar ? { par: coursePar } : {}),
        ...(courseSlope ? { slope: courseSlope } : {}),
        ...(courseRating ? { rating: courseRating } : {}),
      };

      const addNewClubCoursePayload: any = { club: clubObject, course: courseObject };
      dispatch(clubsActions.createClubAndCourse(addNewClubCoursePayload));
    }

  };

  const onLocationSubmit = () => {

    if (!club) {
      throw new Error(t("AddGolfCoursePage.NoClubData"));
    }

    //if there is a change in
    const {
      location,
      formattedName,
      addressOne,
      city,
      postcode,
      provinceName,
      countryName,
    } = club;

    const clubLat = location?.coordinates[1] || 0;
    const clublong = location?.coordinates[0] || 0;

    if ((clubLocationLat == 0 && clubLocationLong == 0) || formattedClubName.length == 0) {

      updateClubLocationErr(t("AddGolfCoursePage.HolesRequiredError"));

    } else {

      let updatePayload: any = {
        ...(clublong != clubLocationLong
          ? { coordinateLong: clubLocationLong }
          : {}),
        ...(clubLocationLat != clubLat ? { coordinateLat: clubLocationLat } : {}),
        ...(formattedName != formattedClubName
          ? { formattedName: formattedClubName }
          : {}),
        ...(addressOne != clubAddressOne ? { addressOne: clubAddressOne } : {}),
        ...(city != clubCity ? { city: clubCity } : {}),
        ...(provinceName != clubProvince ? { provinceName: clubProvince } : {}),
        ...(postcode != clubPostalCode ? { postcode: clubPostalCode } : {}),
        ...(countryName != clubCountry ? { countryName: clubCountry } : {}),
      };

      if (Object.keys(updatePayload).length) {
        updatePayload["_id"] = club._id;
        updatePayload["clubEditStep"] = CLUB_LOCATION_MAP_STEP;
        dispatch(clubsActions.updateClubData(updatePayload));
      } else {  // if there is nothing to update then move to next step
        moveToNextStep();
      }

    }

  };

  const onClubDescriptionSubmit = () => {

    if (!club) {
      throw new Error(t("AddGolfCoursePage.NoClubData"));
    }

    const { description } = club;

    if (clubDescription !== description) {
      let updatePayload: any = {
        _id: club._id,
        description: clubDescription?.trim(),
        clubEditStep: CLUB_DESCRIPTION_STEP
      };
      dispatch(clubsActions.updateClubData(updatePayload));
    } else {  // if there is nothing to update then move to next step
      moveToNextStep();
    }
  };

  const onBasicSubmit = () => {

    if (!course) {
      throw new Error(t("AddGolfCoursePage.NoCourseData"));
    }

    const { holes, par, distance, slope, rating } = course;

    let courseUpdatePayload: any = {
      ...(holes != courseHoles ? { holes: courseHoles } : {}),
      ...(par != coursePar ? { par: coursePar } : {}),
      ...(distance != courseDistance ? { distance: courseDistance } : {}),
      ...(slope != courseSlope ? { slope: courseSlope } : {}),
      ...(rating != courseRating ? { rating: courseRating } : {}),
    };

    if (Object.keys(courseUpdatePayload).length) {

      courseUpdatePayload["_id"] = course._id;
      dispatch(coursesActions.updateCourseData(courseUpdatePayload));

      // update club active step 
      if (club && club?._id) {
        const updatePayload: any = {
          _id: club._id,
          clubEditStep: COURSE_BASICS_STEP
        };
        dispatch(clubsActions.updateClubData(updatePayload));
      }

    } else {  // if there is nothing to update then move to next step
      moveToNextStep();
    }
  };

  const onFacilitiesSubmit = () => {

    if (!club) {
      throw new Error(t("AddGolfCoursePage.NoClubData"));
    }

    const {
      walkingAllowed,
      metalSpikesAllowed,
      dressCode,
      cartsForRentalAvailable,
      caddiesForRentalAvailable,
      drivingRange,
      pitchingArea,
      puttingGreen,
      teachingPro,
      clubsForRentalAvailable,
    } = club;

    let updatePayload: any = {
      _id: club._id,
      clubEditStep: CLUB_FACILITIES_STEP,
      ...(walkingAllowed != clubWalkingAround
        ? { walkingAllowed: clubWalkingAround }
        : {}),
      ...(metalSpikesAllowed != clubMetalSpikesAllowed
        ? { metalSpikesAllowed: clubMetalSpikesAllowed }
        : {}),
      ...(dressCode != clubDressCodeRequired
        ? { dressCode: clubDressCodeRequired }
        : {}),
      ...(cartsForRentalAvailable != clubCartsProvided
        ? { cartsForRentalAvailable: clubCartsProvided }
        : {}),
      ...(clubsForRentalAvailable != clubGolfClubsProvided
        ? { clubsForRentalAvailable: clubGolfClubsProvided }
        : {}),
      ...(caddiesForRentalAvailable != clubCaddiesAvailable
        ? { caddiesForRentalAvailable: clubCaddiesAvailable }
        : {}),
      ...(drivingRange != clubDrivingRangePresent
        ? { drivingRange: clubDrivingRangePresent }
        : {}),
      ...(pitchingArea != clubPitchingAreaPresent
        ? { pitchingArea: clubPitchingAreaPresent }
        : {}),
      ...(puttingGreen != clubPuttingGreen
        ? { puttingGreen: clubPuttingGreen }
        : {}),
      ...(teachingPro != clubTeachingPro
        ? { teachingPro: clubTeachingPro }
        : {}),
    };

    dispatch(clubsActions.updateClubData(updatePayload));
  };

  const onImagesSubmit = () => {

    if (!club) {
      throw new Error(t("AddGolfCoursePage.NoClubData"));
    }

    const { photos = [] } = club;
    const clubPhotos = photos.map(({ _id }) => _id);
    const viewPhotos = merchantImages.map(({ _id }) => _id);

    const areViewPhotosDifferent =
      clubPhotos.length !== viewPhotos.length ||
      viewPhotos.filter((viewId) => !clubPhotos.includes(viewId)).length > 0;
    if (areViewPhotosDifferent) {
      const updatePayload: any = {
        _id: club._id,
        photos: viewPhotos,
        clubEditStep: CLUB_PICTURES_STEP
      };
      dispatch(clubsActions.updateClubData(updatePayload));
    } else { // if there is nothing to update then move to next step
      moveToNextStep();
    }
  };

  const onLocationTextChange = (event: any) => {
    console.log("On locatin text change---", event.target.value)
    setformattedClubName(event.target.value);
    setClubLocationLat(0);
    setclubLocationLong(0);
    updateClubLocationErr("");
  }

  // steps to follow after selecting place from auto complete
  const autoCompleteOnPlaceSelect = (placeObject: any) => {

    const {
      address_components = [],
      geometry,
      formatted_address,
    } = placeObject;

    // extract address components from 'address_components'

    const lat = geometry.location.lat();
    const lng = geometry.location.lng();
    let addressLineOne = "";

    const streetObject = address_components.find((obj: any) =>
      obj.types.includes("street_number")
    );
    const routeObject = address_components.find((obj: any) =>
      obj.types.includes("route")
    );
    const localityObject = address_components.find((obj: any) =>
      obj.types.includes("locality")
    );
    const postalTownObject = address_components.find((obj: any) =>
      obj.types.includes("postal_town")
    );
    const adminAreaLevel2Object = address_components.find((obj: any) =>
      obj.types.includes("administrative_area_level_2")
    );
    const sublocalityObject = address_components.find((obj: any) =>
      obj.types.includes("sublocality") || obj.types.includes("sublocality_level_1")
    );
    const neighborhoodObject = address_components.find((obj: any) =>
      obj.types.includes("neighborhood")
    );
    const stateObject = address_components.find((obj: any) =>
      obj.types.includes("administrative_area_level_1")
    );
    const countryObject = address_components.find((obj: any) =>
      obj.types.includes("country")
    );
    const postalCodeObject = address_components.find((obj: any) =>
      obj.types.includes("postal_code")
    );

    if (streetObject && streetObject["long_name"]) {
      addressLineOne = streetObject["long_name"];
    }

    if (routeObject && routeObject["long_name"]) {
      addressLineOne = `${addressLineOne} ${routeObject["long_name"]}`;
    }

    // save city separately (prefer locality, then postal_town, then sublocality/neighborhood, then admin level 2)
    const detectedCity =
      (localityObject && localityObject["long_name"]) ||
      (postalTownObject && postalTownObject["long_name"]) ||
      (sublocalityObject && sublocalityObject["long_name"]) ||
      (neighborhoodObject && neighborhoodObject["long_name"]) ||
      (adminAreaLevel2Object && adminAreaLevel2Object["long_name"]) ||
      "";

    if (detectedCity) {
      setClubCity(detectedCity);
    }

    if (addressLineOne && addressLineOne.trim().length) {
      setClubAddressOne(addressLineOne);
    }

    if (stateObject && stateObject["long_name"]) {
      setClubProvince(stateObject["long_name"]);
    }

    if (countryObject && countryObject["long_name"]) {
      setClubCountry(countryObject["long_name"]);
    }

    if (postalCodeObject && postalCodeObject["long_name"]) {
      setClubPostalCode(postalCodeObject["long_name"]);
    }

    if (formatted_address) {
      setformattedClubName(formatted_address);
    }

    if (lat) {
      setClubLocationLat(lat);
    }

    if (lng) {
      setclubLocationLong(lng);
    }

    updateClubLocationErr("");
  };

  const mapOptions = {
    fullscreenControl: false,
    streetViewControl: false,
    mapTypeControl: false,
    zoomControl: false,
  };

  const onTitleChange = (title: string) => {
    setClubDisplayName(title);
    dispatch(externalClubsActions.externalClubsList({ text: title }));
  };

  const onTitleSelect = (externalClubObject: ExternalClubPayload) => {
    const {
      name = "",
      address = "",
      countryName = "",
      provinceName = "",
      par = 0,
      slope = 0,
      length = 0,
      rating = 0
    } = externalClubObject;
    setClubAddressOne(address);
    setClubDisplayName(name);
    setClubCountry(countryName);
    setClubProvince(provinceName);
    setCoursePar(par);
    setCourseSlope(slope);
    setCourseRating(rating);
    setCourseDistance(length);
    toggleExternalClubModal(false);
  };

  const onRemoveMerchantImage = (imageId?: string) => {
    const updatedImages = merchantImages.filter(
      (imageObj) => imageObj._id !== imageId
    );
    dispatch(merchantFilesActions.merchantFilesUpdate(updatedImages));
  };

  const onHandleImageSelect = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 5) {
      toast.error(t("AddGolfCoursePage.MoreImageError"));
    } else if (
      e.target.files &&
      e.target.files.length > 0 &&
      e.target.files.length <= 5
    ) {
      const formData = new FormData();

      for (let index = 0; index < e.target.files.length; index++) {
        const file = e.target.files[index];
        formData.append("files", file);
      }

      dispatch(merchantFilesActions.merchantFilesUpload(formData));
    }
  };

  const moveToNextStep = () => {

    // if step is passed i.e. edit mode is enabled then on move to next step go to the "courses" screen
    if (!!editStep) {
      navigateToPath("/courses");
    } else {
      // move to next step only when edit step is not passed i.e. course is being created
      const nextStep = (activeStep + 1);
      handleTileChange(nextStep);
      setStep(nextStep);
    }
  };

  const onDiscountCompletion = () => {
    // mark discount step completed
    if (club && club?._id) {
      const updatePayload: any = {
        _id: club?._id,
        clubEditStep: COURSE_DISCOUNT_STEP
      };
      dispatch(clubsActions.updateClubData(updatePayload));
    }
  }

  const onClubTimingsSubmit = (updatePayload: any) => {
    if (club && club._id) {
      updatePayload["_id"] = club._id;
      updatePayload["clubEditStep"] = COURSE_TIME_AND_DAYS_STEP;
      dispatch(clubsActions.updateClubData(updatePayload))
    }
  }

  const nextStep = () => {
    if (activeStep == CLUB_ACCOUNT_NAME_STEP) {
      onClubAccountNameSubmit();
    } else if (activeStep == CLUB_DESCRIPTION_STEP) {
      onClubDescriptionSubmit();
    } else if (activeStep == COURSE_BASICS_STEP) {
      onBasicSubmit();
    } else if (activeStep == CLUB_FACILITIES_STEP) {
      onFacilitiesSubmit();
    } else if (activeStep == CLUB_PICTURES_STEP) {
      onImagesSubmit();
    }
  };

  useEffect(() => {

    // club name and club location required to show prompt
    const showPrompt = !!club && !!club._id && (!!!club?.name || !!!club?.location);
    setRequestedDataIsNotFilled(showPrompt);

    if (activeStep == CLUB_ACCOUNT_NAME_STEP) {
      if (club) {
        const {
          name: clubName = "",
          addressOne = "",
          provinceName = "",
          postcode = "",
        } = club;

        setClubDisplayName(clubName);
        setClubAddressOne(addressOne);
        setClubPostalCode(postcode);
        setClubProvince(provinceName);

        resetClubDisplayName({ clubDisplayName: clubName });
        dispatch(externalClubsActions.resetExternalClubs());
      }
    } else if (activeStep == CLUB_LOCATION_MAP_STEP) {
      if (club) {
        const {
          location: clubLocation,
          formattedName = "",
          addressOne = "",
          city = "",
          provinceName = "",
          postcode = "",
          countryName = "",
          name = ""
        } = club;

        setformattedClubName(formattedName);
        setClubLocationLat(
          clubLocation && clubLocation.coordinates
            ? clubLocation.coordinates[1]
            : 0
        );
        setclubLocationLong(
          clubLocation && clubLocation.coordinates
            ? clubLocation.coordinates[0]
            : 0
        );
        setClubAddressOne(addressOne);
        setClubCity(city);
        setClubPostalCode(postcode);
        setClubProvince(provinceName);
        setClubCountry(countryName);
      }
    } else if (activeStep == CLUB_DESCRIPTION_STEP) {
      if (club) {
        const { description = "" } = club;
        setClubDescription(description);
        resetDescription({ clubDescription: description }); // reset form data on load
      }
    } else if (activeStep == COURSE_BASICS_STEP) {
      if (course) {
        const { holes = 9, par = 0, distance = 0, slope = 0, rating = 0 } = course;
        setcourseHoles(holes);
        setCoursePar(par);
        setCourseDistance(distance);
        setCourseSlope(slope);
        setCourseRating(rating);
        resetBasicSteps({
          courseDistance: distance,
          courseHoles: holes,
          coursePar: par,
          courseSlope: slope,
          courseRating: rating
        });
      }
    } else if (activeStep == CLUB_FACILITIES_STEP) {
      if (club) {
        const {
          walkingAllowed = false,
          metalSpikesAllowed = false,
          dressCode = false,
          cartsForRentalAvailable = false,
          clubsForRentalAvailable = false,
          caddiesForRentalAvailable = false,
          drivingRange = false,
          pitchingArea = false,
          puttingGreen = false,
          teachingPro = false,
        } = club;
        setClubWalkingAround(walkingAllowed);
        setClubMetalSpikesAllowed(metalSpikesAllowed);
        setClubDressCodeRequired(dressCode);
        setClubCartsProvided(cartsForRentalAvailable);
        setClubGolfClubsProvided(clubsForRentalAvailable);
        setClubDrivingRangePresent(drivingRange);
        setClubCaddiesAvailable(caddiesForRentalAvailable);
        setClubPitchingAreaPresent(pitchingArea);
        setClubPuttingGreen(puttingGreen);
        setClubTeachingPro(teachingPro);
        resetClubAmenities({
          clubWalkingAround: walkingAllowed,
          clubMetalSpikesAllowed: metalSpikesAllowed,
          clubDressCodeRequired: dressCode,
          clubCartsProvided: cartsForRentalAvailable,
          clubGolfClubsProvided: clubsForRentalAvailable,
          clubCaddiesAvailable: caddiesForRentalAvailable,
          clubDrivingRangePresent: drivingRange,
          clubPitchingAreaPresent: pitchingArea,
          clubPuttingGreen: puttingGreen,
          clubTeachingPro: teachingPro,
        });
      }
    } else if (activeStep == CLUB_PICTURES_STEP) {
      // reset selected merchant images
      dispatch(merchantFilesActions.resetSelectedMerchantFile());

      //initialise merchant images
      if (club) {
        const { photos = [] } = club;

        dispatch(merchantFilesActions.merchantFilesUpdate(photos));
      }
    } else if (activeStep == COURSE_DISCOUNT_STEP) {
      dispatch(discountActions.resetAllDiscountStatus())
      if (course) {
        dispatch(discountActions.fetchDiscountProfile(course._id))
      }
    }
  }, [activeStep, club, course]);

  useEffect(() => {
    // when the created club and course is passed and the status is success then only move to next step 
    if (createClubCourseStatus === SAGA_STATUS_SUCCESS && club && course) {
      dispatch(clubsActions.resetCreateClubAndCourseStatus());
      moveToNextStep();
    }
  }, [createClubCourseStatus, club, course])

  useEffect(() => {

    // if club data is udated and edit step is passed then show toast on successfully on update
    if (updateClubStatus === SAGA_STATUS_SUCCESS && editStep) {
      toast.info(t("AddGolfCoursePage.ClubInfoSuccessUpdate"));
    }

    if (
      updateClubStatus === SAGA_STATUS_SUCCESS &&
      [
        CLUB_ACCOUNT_NAME_STEP,
        CLUB_DESCRIPTION_STEP,
        CLUB_FACILITIES_STEP,
        CLUB_PICTURES_STEP,
        COURSE_TIME_AND_DAYS_STEP,
        CLUB_LOCATION_MAP_STEP,
        COURSE_DISCOUNT_STEP,
        COURSE_BASICS_STEP
      ].includes(activeStep)
    ) {
      dispatch(clubsActions.resetUpdateClubDataStatus());
      moveToNextStep();
    }
  }, [updateClubStatus, activeStep]);

  return (
    <>
      <Prompt
        show={requestedDataIsNotFilled}
        message={t("AddGolfCoursePage.LeavingSoSoonText")}
      />

      <div className="addGolfCourseWrapper">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-12 col-lg-7">
              <div className="formWrap mt-4">
                {activeStep == CLUB_ACCOUNT_NAME_STEP && (
                  <div className="formStepper mt-5 pt-4">
                    <h5 className="mb-4 fw700">{t("AddGolfCoursePage.title")}</h5>
                    <form
                      onSubmit={handleClubDisplayNameSubmit(
                        onClubDisplayNameSubmit
                      )}
                    >
                      <div className="form-floating mt-4 mb-5">
                        <Controller
                          control={clubDisplayNameControl}
                          name="clubDisplayName"
                          render={({ field: { onChange } }) => (
                            <>
                              <input
                                disabled={(club && club.name ? club.name : "").length > 0}
                                type="text"
                                className="form-control"
                                id="title"
                                placeholder={t("AddGolfCoursePage.AddTitleLabel")}
                                value={clubDisplayName}
                                {...clubDisplayNameRegister("clubDisplayName", {
                                  required: true,
                                  validate: (value) => {
                                    return value.trim() !== "";
                                  },
                                })}
                                onChange={({ target }) => {
                                  onChange(target.value);
                                  onTitleChange(target.value);
                                }}
                                onBlur={(e) => {
                                  e.relatedTarget === null &&
                                    toggleExternalClubModal(
                                      !showExternalClubModal
                                    );
                                }}
                                onFocus={(e) => {
                                  e.relatedTarget === null &&
                                    toggleExternalClubModal(
                                      !showExternalClubModal
                                    );
                                }}
                              />
                              <label htmlFor="title"> {t("AddGolfCoursePage.AddTitleLabel")}</label>
                              <div className="listActionDropdownBlock">
                                <Dropdown.Menu
                                  show={
                                    externalClubs.length > 0 &&
                                    showExternalClubModal
                                  }
                                  className="form-control clubname-dropdown-menu"
                                >
                                  {externalClubs.map((obj) => (
                                    <Dropdown.Item
                                      key={obj._id}
                                      onClick={() => {
                                        onChange(obj.name);
                                        onTitleSelect(obj);
                                      }}
                                    >
                                      {obj.name}{(obj.address ? `, (${obj.address})` : ``)}
                                    </Dropdown.Item>
                                  ))}
                                </Dropdown.Menu>
                              </div>
                            </>
                          )}
                        />
                        {(clubDisplayNameError?.clubDisplayName?.type === "required" || clubDisplayNameError?.clubDisplayName?.type === "validate") && (
                          <span className="isInvalidMessage text-danger">
                            {t("AddGolfCoursePage.requiredError")}
                          </span>
                        )}
                      </div>
                      <div className="pt-5">
                        <button
                          className="button button-primary button-rounded button-min-150 fw700"
                          type="submit"
                        >
                          {t("AddGolfCoursePage.Next")}
                        </button>
                      </div>
                    </form>
                  </div>
                )}
                {activeStep == CLUB_LOCATION_MAP_STEP && (
                  <form>
                    <div className="formStepper">
                      <h3 className="mb-2">{t("AddGolfCoursePage.LocationTitle")}</h3>
                      {isLoaded && (
                        <div className="map">
                          <GoogleMap
                            mapContainerStyle={{
                              width: "600px",
                              height: "350px",
                            }}
                            center={{
                              lat: clubLocationLat,
                              lng: clubLocationLong,
                            }}
                            options={mapOptions}
                            zoom={15}
                            // onLoad={data => console.log(data)}
                            // onUnmount={onUnmount}
                            onCenterChanged={() => { }}
                          >
                            <MarkerF
                              position={{
                                lat: clubLocationLat,
                                lng: clubLocationLong,
                              }}
                              icon={{
                                url: "./assets/images/icons/marker.svg",
                              }}
                              zIndex={10}
                              draggable={false}
                            />
                          </GoogleMap>
                          <div className="searchBox">
                            <em className="icon-location"></em>
                            <Autocomplete
                              apiKey={
                                "AIzaSyDoMcuVhHHP8fos2Vp3PdtdZNEaSYHq9kk"
                              }
                              className="form-control control-xl disable"
                              onPlaceSelected={autoCompleteOnPlaceSelect}
                              defaultValue={formattedClubName}
                              onChange={onLocationTextChange}
                              options={{
                                types: ["address"],
                                componentRestrictions: {
                                  country: ["CA", "US", "DO", "MX", "GB"],
                                },
                              }}
                            />
                          </div>
                        </div>
                      )}
                      <div className="text-center _mapNoteText mt-2">
                        {t("AddGolfCoursePage.LocationError")}
                      </div>
                      {
                        (clubLocationErr.length) ? (
                          <span className="isInvalidMessage text-danger">
                            {clubLocationErr}
                          </span>
                        ) : <></>
                      }
                      <div className="pt-4">
                        <button
                          className="button button-primary button-rounded button-min-150 fw700"
                          type="button"
                          onClick={onLocationSubmit}
                        >
                          {!!editStep ? t("AddGolfCoursePage.Save") : t("AddGolfCoursePage.Next")}
                        </button>
                      </div>
                    </div>
                  </form>
                )}
                {activeStep == CLUB_PICTURES_STEP && (
                  <form className="formStepper" onSubmit={(event) => { event.preventDefault(); nextStep(); }}>
                    <h5 className="mb-4">{t("AddGolfCoursePage.UploadVisualTitle")}</h5>
                    <label
                      className={`uploadImageWrap mb-0 d-block ${disableBgColor}`}
                    >
                      <input
                        type="file"
                        disabled={
                          merchantImages.length === ALLOWED_MERCHANT_IMAGES
                        }
                        accept="image/*"
                        onChange={onHandleImageSelect}
                        onClick={(event: any) => { event.target.value = '' }}
                        multiple
                      />
                      <div className="inner" id="dropzone" key={getUniqueString()}>
                        <img
                          src="../assets/images/icons/gallery.svg"
                          alt="gallery"
                        />
                        <h5>{t("AddGolfCoursePage.ClickHereTitle")}</h5>
                        <p>{t("AddGolfCoursePage.MinImagesValidationError")}</p>
                        {/* <label className="_link mb-0 mt-5">
                          Upload from your device
                        </label> */}
                      </div>
                    </label>
                    <div className="row gutters-7 my-4">
                      {merchantImages.map((image: MerchantFiles) => (
                        <div className="col-auto" key={image._id}>
                          <div className="uploadedImage">
                            <span>
                              <img
                                src={image.path}
                                alt=""
                                className="img-fluid"
                              />
                            </span>
                            <a
                              href={void 0}
                              onClick={() =>
                                onRemoveMerchantImage(image?._id)
                              }
                            >
                              <em className="fa-solid fa-close"></em>
                            </a>
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="pt-4">
                      <button
                        className="button button-primary button-rounded button-min-150 fw700"
                        type="submit"
                        disabled={(merchantImages.length < 2 || merchantImages.length > 5)}
                      >
                        {!!editStep ? t("AddGolfCoursePage.Save") : t("AddGolfCoursePage.Next")}
                      </button>
                    </div>
                  </form>
                )}
                {activeStep == CLUB_DESCRIPTION_STEP && (
                  <div className="formStepper">
                    <form
                      onSubmit={handleDescriptionSubmit(onDescriptionSubmit)}
                    >
                      <h5 className="mb-4">
                        {t("AddGolfCoursePage.PlaceSpecialityTitle")}
                      </h5>
                      <div className="form-group mb-2">
                        <div className="form-floating">
                          <textarea
                            className="form-control"
                            rows={6}
                            id="shareSpecial"
                            placeholder={t("AddGolfCoursePage.SaySomethingPlaceholder")}
                            style={{ height: "120px" }}
                            value={(clubDescription || "")}
                            {...descriptionRegister("clubDescription", {
                              required: true,
                              validate: (value) => {
                                return value.trim() !== "";
                              },
                            })}
                            onChange={({ target }) =>
                              setClubDescription(target.value)
                            }
                          ></textarea>
                          <label htmlFor="shareSpecial">{t("AddGolfCoursePage.SaySomethingLabel")}</label>
                        </div>
                      </div>
                      {descriptionError.clubDescription?.type && (
                        <span className="isInvalidMessage text-danger">
                          {t("AddGolfCoursePage.clubDescriptionError")}
                        </span>
                      )}
                      <div className="pt-4">
                        <button
                          className="button button-primary button-rounded button-min-150 fw700"
                          type="submit"
                        >
                          {!!editStep ? t("AddGolfCoursePage.Save") : t("AddGolfCoursePage.Next")}
                        </button>
                      </div>
                    </form>
                  </div>
                )}
                {activeStep == COURSE_BASICS_STEP && (
                  <div className="formStepper">
                    <form onSubmit={handleCourseBasicSubmit(onClubBasicSubmit)}>
                      <h5 className="mb-4">{t("AddGolfCoursePage.AddDetailsTitle")}</h5>
                      <ul className="courseFormDetail">
                        <li>
                          <div className="row gutters-5 align-items-center">
                            <div className="col">
                              <label className="mb-0 control-label">
                                {t("AddGolfCoursePage.Holes")}
                              </label>
                            </div>
                            <div className="col-auto">
                              <label className="ch-radio ch-radio-border my-1 me-3">
                                <input
                                  type="radio"
                                  checked={courseHoles == 9}
                                  onClick={() => setcourseHoles(9)}
                                  value={9}
                                />
                                <span className="fw700">{t("AddGolfCoursePage.9Holes")}</span>
                              </label>
                              <label className="ch-radio ch-radio-border my-1">
                                <input
                                  type="radio"
                                  checked={courseHoles == 18}
                                  onClick={() => setcourseHoles(18)}
                                  value={18}
                                />
                                <span className="fw700">{t("AddGolfCoursePage.18Holes")}</span>
                              </label>
                            </div>
                            {basicErrors.courseHoles?.type == "required" && (
                              <span className="isInvalidMessage text-danger text-end">
                                {t("AddGolfCoursePage.HolesRequiredError")}
                              </span>
                            )}
                          </div>
                        </li>
                        <li>
                          <div className="row gutters-5 align-items-center">
                            <div className="col">
                              <label className="mb-0 control-label">{t("AddGolfCoursePage.Par")}</label>
                            </div>
                            {/* <div className="col-auto"> */}
                            <input
                              type="text"
                              className="form-control control-xl"
                              placeholder={t("AddGolfCoursePage.AddHerePlaceholder")}
                              value={coursePar}
                              {...basicRegister("coursePar", {
                                required: true,
                                validate: (value) => Number(value) > 0
                              })}
                              onChange={({ target }) => {
                                const value = Number(target.value);
                                if (!isNaN(value) && value > 0) {
                                  setCoursePar(value);
                                } else {
                                  setCoursePar(0);
                                }
                              }}
                            />
                            {basicErrors.coursePar?.type == "required" && (
                              <p className="isInvalidMessage text-danger text-end">
                                {t("AddGolfCoursePage.ParRequiredError")}
                              </p>
                            )}

                            {basicErrors.coursePar?.type === "validate" && (
                              <p className="isInvalidMessage text-danger text-end">
                                {t("AddGolfCoursePage.ParValidationError")}
                              </p>
                            )}
                          </div>
                          {/* </div> */}
                        </li>
                        <li>
                          <div className="row gutters-5 align-items-center">
                            <div className="col">
                              <label className="mb-0 control-label">
                                {t("AddGolfCoursePage.Slope")}
                              </label>
                            </div>
                            {/* <div className="col-auto"> */}
                            <input
                              type="text"
                              className="form-control control-xl"
                              placeholder={t("AddGolfCoursePage.AddHerePlaceholder")}
                              value={courseSlope}
                              {...basicRegister("courseSlope", {
                                required: true,
                                validate: (value: any) => parseFloat(value) > 0

                              })}
                              onChange={({ target }) => {
                                const value: any = target.value;
                                // Allow whole numbers or decimals with up to 2 digits
                                if (value === '' || /^\d+(\.\d{0,2})?$/.test(value)) {
                                  setCourseSlope(value);
                                }
                              }
                              }
                            />
                            {basicErrors.courseSlope?.type == "required" && (
                              <p className="isInvalidMessage text-danger text-end">
                                {t("AddGolfCoursePage.SlopeRequiredError")}
                              </p>
                            )}

                            {basicErrors.courseSlope?.type === "validate" && (
                              <p className="isInvalidMessage text-danger text-end">
                                {t("AddGolfCoursePage.SlopeValidationError")}
                              </p>
                            )}
                          </div>
                          {/* </div> */}
                        </li>

                        {/* Rating */}

                        <li>
                          <div className="row gutters-5 align-items-center">
                            <div className="col">
                              <label className="mb-0 control-label">{t("AddGolfCoursePage.Rating")}</label>
                            </div>
                            {/* <div className="col-auto"> */}
                            <input
                              type="number"
                              className="form-control control-xl"
                              placeholder={t("AddGolfCoursePage.AddHerePlaceholder")}
                              onWheel={(e:any) => e.target?.blur()}
                              value={courseRating}
                              {...basicRegister("courseRating", {
                                required: true,
                                validate: (value) => Number(value) > 0
                              })}
                              onChange={({ target }) => {
                                const value = Number(target.value);
                                if (!isNaN(value) && value > 0) {
                                  setCourseRating(value);
                                } else {
                                  setCourseRating(0);
                                }
                              }}
                            />
                            {basicErrors.courseRating?.type == "required" && (
                              <p className="isInvalidMessage text-danger text-end">
                                {t("AddGolfCoursePage.RatingRequiredError")}
                              </p>
                            )}

                            {basicErrors.courseRating?.type === "validate" && (
                              <p className="isInvalidMessage text-danger text-end">
                                {t("AddGolfCoursePage.RatingValidationError")}
                              </p>
                            )}
                          </div>
                          {/* </div> */}
                        </li>
                        <li>
                          <div className="row gutters-5 align-items-center">
                            <div className="col">
                              <label className="mb-0 control-label">
                                {t("AddGolfCoursePage.Length")}{" "}
                                <span className="color-600 fw400">({t("AddGolfCoursePage.yards")})</span>
                              </label>
                            </div>
                            {/* <div className="col-auto"> */}
                            <input
                              type="text"
                              className="form-control control-xl"
                              placeholder="Add here"
                              value={courseDistance}
                              {...basicRegister("courseDistance", {
                                required: true,
                                validate: (value) => Number(value) > 0
                              })}
                              onChange={({ target }) => {
                                const value = Number(target.value);
                                if (!isNaN(value) && value > 0) {
                                  setCourseDistance(value);
                                } else {
                                  setCourseDistance(0);
                                }
                              }}
                            />
                            {basicErrors.courseDistance?.type ==
                              "required" && (
                                <p className="isInvalidMessage text-danger text-end">
                                  {t("AddGolfCoursePage.DistanceRequiredError")}
                                </p>
                              )}

                            {basicErrors.courseDistance?.type ==
                              "validate" && (
                                <p className="isInvalidMessage text-danger text-end">
                                  {t("AddGolfCoursePage.DistanceValidationError")}
                                </p>
                              )}
                          </div>
                          {/* </div> */}
                        </li>




                      </ul>
                      <div className="pt-4">
                        <button
                          type="submit"
                          className="button button-primary button-rounded button-min-150 fw700"
                        >
                          {!!editStep ? t("AddGolfCoursePage.Save") : t("AddGolfCoursePage.Next")}
                        </button>
                      </div>
                    </form>
                  </div>
                )}
                {activeStep == CLUB_FACILITIES_STEP && (
                  <div className="formStepper">
                    <form
                      onSubmit={(e) => { e.preventDefault(); nextStep(); }}
                    >
                      <h5 className="mb-4">
                        {t("AddGolfCoursePage.AddAmentiesTitle")}
                      </h5>
                      <ul className="courseFormDetail">
                        <li>
                          <h5 className="mb-3">{t("AddGolfCoursePage.Policies")}</h5>
                          <div className="row gutters-5 py-2">
                            <div className="col">
                              <label className="mb-0 control-label fw400 fw400">
                                {t("AddGolfCoursePage.WalkingAllowed")}
                              </label>
                            </div>
                            <div className="col-auto d-flex align-items-center">
                              <label className="ch-radio ch-radio-border me-4 fw700">
                                <input
                                  type="radio"
                                  checked={clubWalkingAround}
                                  onClick={() => setClubWalkingAround(true)}
                                />
                                <span>{t("AddGolfCoursePage.Yes")}</span>
                              </label>
                              <label className="ch-radio ch-radio-border fw700">
                                <input
                                  type="radio"
                                  checked={!clubWalkingAround}
                                  onClick={() => setClubWalkingAround(false)}
                                />
                                <span>{t("AddGolfCoursePage.No")}</span>
                              </label>
                            </div>
                          </div>
                          <div className="row gutters-5 py-2">
                            <div className="col">
                              <label className="mb-0 control-label fw400">
                                {t("AddGolfCoursePage.MetalSpikesAllowed")}
                              </label>
                            </div>
                            <div className="col-auto d-flex align-items-center">
                              <label className="ch-radio ch-radio-border me-4 fw700">
                                <input
                                  type="radio"
                                  checked={clubMetalSpikesAllowed}
                                  onClick={() => setClubMetalSpikesAllowed(true)}
                                />
                                <span>{t("AddGolfCoursePage.Yes")}</span>
                              </label>
                              <label className="ch-radio ch-radio-border fw700">
                                <input
                                  type="radio"
                                  checked={!clubMetalSpikesAllowed}
                                  onClick={() => setClubMetalSpikesAllowed(false)}
                                />
                                <span>{t("AddGolfCoursePage.No")}</span>
                              </label>
                            </div>
                          </div>
                          <div className="row gutters-5 py-2">
                            <div className="col">
                              <label className="mb-0 control-label fw400">
                                {t("AddGolfCoursePage.DressCodeTitle")}
                              </label>
                            </div>
                            <div className="col-auto d-flex align-items-center">
                              <label className="ch-radio ch-radio-border me-4 fw700">
                                <input
                                  type="radio"
                                  checked={clubDressCodeRequired}
                                  onClick={() => setClubDressCodeRequired(true)}
                                />
                                <span>{t("AddGolfCoursePage.Yes")}</span>
                              </label>
                              <label className="ch-radio ch-radio-border fw700">
                                <input
                                  type="radio"
                                  checked={!clubDressCodeRequired}
                                  onClick={() => setClubDressCodeRequired(false)}
                                />
                                <span>{t("AddGolfCoursePage.No")}</span>
                              </label>
                            </div>
                          </div>
                        </li>
                        <li>
                          <h5 className="mb-3 mt-2">{t("AddGolfCoursePage.RentalsOrServices")}</h5>
                          <div className="row gutters-5 py-2">
                            <div className="col">
                              <label className="mb-0 control-label fw400">
                                {t("AddGolfCoursePage.Carts")}
                              </label>
                            </div>
                            <div className="col-auto d-flex align-items-center">
                              <label className="ch-radio ch-radio-border me-4 fw700">
                                <input
                                  type="radio"
                                  checked={clubCartsProvided}
                                  onClick={() => setClubCartsProvided(true)}
                                />
                                <span>{t("AddGolfCoursePage.Yes")}</span>
                              </label>
                              <label className="ch-radio ch-radio-border fw700">
                                <input
                                  type="radio"
                                  checked={!clubCartsProvided}
                                  onClick={() => setClubCartsProvided(false)}
                                />
                                <span>{t("AddGolfCoursePage.No")}</span>
                              </label>
                            </div>
                          </div>
                          <div className="row gutters-5 py-2">
                            <div className="col">
                              <label className="mb-0 control-label fw400">
                                {t("AddGolfCoursePage.Clubs")}
                              </label>
                            </div>
                            <div className="col-auto d-flex align-items-center">
                              <label className="ch-radio ch-radio-border me-4 fw700">
                                <input
                                  type="radio"
                                  checked={clubGolfClubsProvided}
                                  onClick={() => setClubGolfClubsProvided(true)}
                                />
                                <span>{t("AddGolfCoursePage.Yes")}</span>
                              </label>
                              <label className="ch-radio ch-radio-border fw700">
                                <input
                                  type="radio"
                                  checked={!clubGolfClubsProvided}
                                  onClick={() => setClubGolfClubsProvided(false)}
                                />
                                <span>{t("AddGolfCoursePage.No")}</span>
                              </label>
                            </div>
                          </div>
                          <div className="row gutters-5 py-2">
                            <div className="col">
                              <label className="mb-0 control-label fw400">
                                {t("AddGolfCoursePage.CaddiesAvailable")}
                              </label>
                            </div>
                            <div className="col-auto d-flex align-items-center">
                              <label className="ch-radio ch-radio-border me-4 fw700">
                                <input
                                  type="radio"
                                  checked={clubCaddiesAvailable}
                                  onClick={() => setClubCaddiesAvailable(true)}
                                />
                                <span>{t("AddGolfCoursePage.Yes")}</span>
                              </label>
                              <label className="ch-radio ch-radio-border fw700">
                                <input
                                  type="radio"
                                  checked={!clubCaddiesAvailable}
                                  onClick={() => setClubCaddiesAvailable(false)}
                                />
                                <span>{t("AddGolfCoursePage.No")}</span>
                              </label>
                            </div>
                          </div>
                        </li>
                        <li className="border-0">
                          <h5 className="mb-3 mt-2">{t("AddGolfCoursePage.PracticeOrInstruction")}</h5>
                          <div className="row gutters-5 py-2">
                            <div className="col">
                              <label className="mb-0 control-label fw400">
                                {t("AddGolfCoursePage.DrivingRange")}
                              </label>
                            </div>
                            <div className="col-auto d-flex align-items-center">
                              <label className="ch-radio ch-radio-border me-4 fw700">
                                <input
                                  type="radio"
                                  checked={clubDrivingRangePresent}
                                  onClick={() => setClubDrivingRangePresent(true)}
                                />
                                <span>{t("AddGolfCoursePage.Yes")}</span>
                              </label>
                              <label className="ch-radio ch-radio-border fw700">
                                <input
                                  type="radio"
                                  checked={!clubDrivingRangePresent}
                                  onClick={() => setClubDrivingRangePresent(false)}
                                />
                                <span>{t("AddGolfCoursePage.No")}</span>
                              </label>
                            </div>
                          </div>
                          <div className="row gutters-5 py-2">
                            <div className="col">
                              <label className="mb-0 control-label fw400">
                                {t("AddGolfCoursePage.PitchingOrchipping")}
                              </label>
                            </div>
                            <div className="col-auto d-flex align-items-center">
                              <label className="ch-radio ch-radio-border me-4 fw700">
                                <input
                                  type="radio"
                                  checked={clubPitchingAreaPresent}
                                  onClick={() => setClubPitchingAreaPresent(true)}
                                />
                                <span>{t("AddGolfCoursePage.Yes")}</span>
                              </label>
                              <label className="ch-radio ch-radio-border fw700">
                                <input
                                  type="radio"
                                  checked={!clubPitchingAreaPresent}
                                  onClick={() => setClubPitchingAreaPresent(false)}
                                />
                                <span>{t("AddGolfCoursePage.No")}</span>
                              </label>
                            </div>
                            {amenitiesErrors.clubPitchingAreaPresent?.type ==
                              "required" && (
                                <span className="isInvalidMessage text-danger">
                                  {t("AddGolfCoursePage.SelectValueError")}
                                </span>
                              )}
                          </div>
                          <div className="row gutters-5 py-2">
                            <div className="col">
                              <label className="mb-0 control-label fw400">
                                {t("AddGolfCoursePage.PuttingGreen")}
                              </label>
                            </div>
                            <div className="col-auto d-flex align-items-center">
                              <label className="ch-radio ch-radio-border me-4 fw700">
                                <input
                                  type="radio"
                                  checked={clubPuttingGreen}
                                  onClick={() => setClubPuttingGreen(true)}
                                />
                                <span>{t("AddGolfCoursePage.Yes")}</span>
                              </label>
                              <label className="ch-radio ch-radio-border fw700">
                                <input
                                  type="radio"
                                  checked={!clubPuttingGreen}
                                  onClick={() => setClubPuttingGreen(false)}
                                />
                                <span>{t("AddGolfCoursePage.No")}</span>
                              </label>
                            </div>
                          </div>
                          <div className="row gutters-5 py-2">
                            <div className="col">
                              <label className="mb-0 control-label fw400">
                                {t("AddGolfCoursePage.TeachingPro")}
                              </label>
                            </div>
                            <div className="col-auto d-flex align-items-center">
                              <label className="ch-radio ch-radio-border me-4 fw700">
                                <input
                                  type="radio"
                                  checked={clubTeachingPro}
                                  onClick={() => setClubTeachingPro(true)}
                                />
                                <span>{t("AddGolfCoursePage.Yes")}</span>
                              </label>
                              <label className="ch-radio ch-radio-border fw700">
                                <input
                                  type="radio"
                                  checked={!clubTeachingPro}
                                  onClick={() => setClubTeachingPro(false)}
                                />
                                <span>{t("AddGolfCoursePage.No")}</span>
                              </label>
                            </div>
                          </div>
                        </li>
                      </ul>

                      <div className="pt-4 mt-3">
                        <button
                          className="button button-primary button-rounded button-min-150 fw700"
                          type="submit"
                        >
                          {!!editStep ? t("AddGolfCoursePage.Save") : t("AddGolfCoursePage.Next")}
                        </button>
                      </div>
                    </form>
                  </div>
                )}
                {activeStep == COURSE_TIME_AND_DAYS_STEP && (
                  <CourseTimings
                    clubObject={club}
                    onSubmit={onClubTimingsSubmit}
                  />
                )}
                {activeStep == COURSE_DISCOUNT_STEP && course && course?._id && (
                  <AddDiscount
                    courseId={course._id}
                    onContinue={onDiscountCompletion}
                    editClubCourseData={editClubCourseData}
                  />
                )}
                {activeStep == CLUB_LINK_TEE_TIME_BOOKING_SYSTEM_STEP && club && course && (
                  <LinkTeeTimeComponent
                    clubObject={club}
                    courseInfo={course}
                    onNextClick={moveToNextStep}
                  />
                )
                }
                {activeStep == CLUB_PREVIEW_STEP && club && course && (
                  <CoursePreview
                    clubInfo={club}
                    courseInfo={course}
                    navigateToPath={navigateToPath}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default AddGolfCourse;